// Posting engagement feature API client.
// Calls POST /api/v1/posts to create a new post (open posting).
// Types mirror api/schemas/posts.py PostCreate / PostResponse.
import { apiClient } from "@/lib/api-client";

export type PostType = "project" | "post";

/** Project lifecycle stage — project posts only. */
export type ProjectStatus = "poc" | "pilot" | "production";

/** What kind of thing a project post showcases — project posts only. */
export type ProjectType =
  | "Skill"
  | "MCP Server"
  | "Plugin"
  | "Automation"
  | "Application";

export interface BodyBlock {
  position: number;
  text: string;
}

/** Request shape for POST /api/v1/posts.
 *
 * No `division` field — the server derives it from the authenticated
 * author's Member profile (issue #22). A client-supplied division is no
 * longer part of the request contract.
 */
export interface PostCreate {
  type: PostType;
  title: string;
  excerpt: string;
  body: BodyBlock[];
  tags: string[];
  external_url?: string | null; // posts (optional link)
  project_type?: ProjectType | null; // project posts: what kind of thing it showcases
  repo_url?: string | null;     // project posts: GitHub (or other) repo
  rideshare_url?: string | null; // project posts: Rideshare platform link
  status?: ProjectStatus | null; // project posts: lifecycle stage
  posted_to_feed?: boolean; // project posts: deliberate opt-in to the Hub Feed
}

/** Request shape for PUT /api/v1/posts/{id} — edit an existing post.
 *
 * Full-replace semantics (mirrors compose): send the complete editable state.
 * `type`, `author_id`, and `division` are immutable and are NOT part of this
 * shape — the server keeps the originals.
 */
export type PostUpdate = Omit<PostCreate, "type">;

/** Response shape from POST /api/v1/posts. */
export interface PostResponse {
  id: string;
  type: PostType;
  author_id: string | null;
  division: string;
  title: string;
  excerpt: string;
  tags: string[];
  vote_count: number;
  pinned: boolean;
  created_at: string; // ISO 8601 datetime string
  updated_at: string | null; // set only when content was edited after creation
  status: ProjectStatus | null;
  external_url: string | null;
  project_type: ProjectType | null;
  posted_to_feed: boolean;
  training_resource_id: string | null;
  repo_url: string | null;
  rideshare_url: string | null;
}

/**
 * Create a new post.
 *
 * Open posting — the post publishes immediately and appears in the feed
 * without any approval step (PRD §5 item 14).
 *
 * Requires an authenticated session cookie (the browser sends it
 * automatically if the user signed in).  Returns 401 if not authenticated.
 */
export async function createPost(data: PostCreate): Promise<PostResponse> {
  return apiClient<PostResponse>("/api/v1/posts", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
}

/**
 * Edit an existing post (author self-service, or admin moderation).
 *
 * PUT /api/v1/posts/{id}. Full-replace semantics — send the complete editable
 * state. Requires an authenticated session; the server enforces that the caller
 * is the author OR an admin (403 otherwise).
 */
export async function updatePost(postId: string, data: PostUpdate): Promise<PostResponse> {
  return apiClient<PostResponse>(`/api/v1/posts/${encodeURIComponent(postId)}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
}
